import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DoneIcon from "@mui/icons-material/Done";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import "../../GeneralServices/NewConnection/NewConnectionForm.scss";
import { cfpBaseUrl } from "../../../network/urls";
import { useLocation } from "react-router";
import { handleFileUpload } from "../../../services/Functions";
import { CircularProgress, Backdrop } from "@mui/material";
import Error from "../../../components/error/error";
import { useSelector } from "react-redux";
import { documentExtensions, portalName } from "../../../utils/PortalName";
import { Alert, Snackbar } from "@mui/material";

const AdjustLastBillUpdateDocuments = () => {
    const initialValues = {
        iv_applicant_cnic_document_front: null,
        iv_applicant_cnic_document_back: null,
        iv_paid_bill_image: null,
    };

    const [openAlert, setOpenAlert] = useState(false);
    const [alertData, setAlertData] = useState({
        type: "success",
        message: "",
    });

    // Documents from API
    const [documents, setDocuments] = useState({});
    const [documentsLoading, setDocumentsLoading] = useState(true);

    // Thumbnail states (for new uploads)
    const [applicantCnicDocumentFrontThumbnail, setApplicantCnicDocumentFrontThumbnail] = useState("");
    const [applicantCnicDocumentBackThumbnail, setApplicantCnicDocumentBackThumbnail] = useState("");
    const [paidBillImageThumbnail, setPaidBillImageThumbnail] = useState("");

    // Error states
    const [applicantCnicFrontError, setApplicantCnicFrontError] = useState("");
    const [applicantCnicBackError, setApplicantCnicBackError] = useState("");
    const [paidBillImageError, setPaidBillImageError] = useState("");

    const [viewBackdrop, setViewBackdrop] = useState(false);
    const { state } = useLocation();
    const { dialogRowData } = state || {};
    let { token } = useSelector((state) => state.auth);
    const [error, setError] = useState(false);
    const navigate = useNavigate();

    // Track which documents were re-uploaded
    const [cnicFrontReUploaded, setCnicFrontReUploaded] = useState(false);
    const [cnicBackReUploaded, setCnicBackReUploaded] = useState(false);
    const [paidBillReUploaded, setPaidBillReUploaded] = useState(false);

    // Fetch documents from API
    const getBillAdjustmentDocuments = async (appId) => {
        setDocumentsLoading(true);
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "applicationId": appId
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        await fetch(cfpBaseUrl + "billAdjustment/lastPaidBillAdjustment/getDocuments", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.status === "200" && result.data?.length > 0) {
                    const docData = result.data[0];
                    setDocuments(docData);

                    // Set thumbnails from API response
                    if (docData.applicantCNICFront) {
                        setApplicantCnicDocumentFrontThumbnail(docData.applicantCNICFront);
                        values.iv_applicant_cnic_document_front = docData.applicantCNICFront;
                    }
                    if (docData.applicantCNICBack) {
                        setApplicantCnicDocumentBackThumbnail(docData.applicantCNICBack);
                        values.iv_applicant_cnic_document_back = docData.applicantCNICBack;
                    }
                    if (docData.paidBillImage) {
                        setPaidBillImageThumbnail(docData.paidBillImage);
                        values.iv_paid_bill_image = docData.paidBillImage;
                    }
                } else {
                    setDocuments({});
                }
                setDocumentsLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching documents:", error);
                setDocuments({});
                setDocumentsLoading(false);
            });
    };

    useEffect(() => {
        if (dialogRowData?.applicationId) {
            getBillAdjustmentDocuments(dialogRowData.applicationId);
        }
    }, [dialogRowData]);

    const { values, handleChange, handleSubmit } = useFormik({
        initialValues,
        validationSchema: Yup.object({
            iv_applicant_cnic_document_front: Yup.mixed().required(
                "Please upload CNIC front in jpg, jpeg, png or pdf format."
            ),
            iv_applicant_cnic_document_back: Yup.mixed().required(
                "Please upload CNIC back in jpg, jpeg, png or pdf format."
            ),
        }),
        onSubmit: async () => {
            setViewBackdrop(true);

            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            myHeaders.append("Authorization", token);

            // Determine flags based on whether document was re-uploaded or keep original
            const cnicFrontFlag = cnicFrontReUploaded ? 2 : documents?.applicantCNICFrontFlag;
            const cnicBackFlag = cnicBackReUploaded ? 2 : documents?.applicantCNICBackFlag;
            const paidBillFlag = paidBillReUploaded ? 2 : (documents?.paidBillImageFlag || "");

            const raw = JSON.stringify({
                "applicationId": dialogRowData.applicationId,
                "submittedThrough": portalName,
                "applicationProgressId": 1,
                "objectionStatus": 0,
                "remarks": "Documents Re-Uploaded",
                "remarksDetail": "",
                "applicantCNICBack": applicantCnicDocumentBackThumbnail,
                "applicantCNICFront": applicantCnicDocumentFrontThumbnail,
                "paidBillImage": paidBillImageThumbnail || "",
                "applicantCNICFrontFlag": 2,
                "applicantCNICBackFlag": 2,
                "paidBillImageFlag": 2

            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow",
            };

            await fetch(cfpBaseUrl + "billAdjustment/lastPaidBillAdjustment/reUploadDocuments", requestOptions)
                .then((response) => response.json())
                .then((result) => {
                    if (result.status === "200") {
                        setViewBackdrop(false);

                        setAlertData({
                            type: "success",
                            message: result.message || "Documents re-uploaded successfully.",
                        });
                        setOpenAlert(true);

                        setTimeout(() => {
                            navigate("/adjust-last-bill-table");
                        }, 3000);
                    } else {
                        setViewBackdrop(false);

                        setAlertData({
                            type: "error",
                            message: result.message || "Something went wrong.",
                        });
                        setOpenAlert(true);
                    }
                })
                .catch(() => {
                    setViewBackdrop(false);
                    setAlertData({
                        type: "error",
                        message: "Failed to re-upload documents.",
                    });
                    setOpenAlert(true);
                });
        },
    });

    const ApplicantCnicDocumentFront = (event) => {
        const newFileName = "copyOfApplicantCNICFront." + event.target.files[0].name.split(".").pop();
        handleFileUpload(
            event,
            handleChange,
            () => { },
            setApplicantCnicFrontError,
            setApplicantCnicDocumentFrontThumbnail,
            newFileName
        );
        setCnicFrontReUploaded(true);
    };

    const ApplicantCnicDocumentBack = (event) => {
        const newFileName = "copyOfApplicantCNICBack." + event.target.files[0].name.split(".").pop();
        handleFileUpload(
            event,
            handleChange,
            () => { },
            setApplicantCnicBackError,
            setApplicantCnicDocumentBackThumbnail,
            newFileName
        );
        setCnicBackReUploaded(true);
    };

    const PaidBillImageUpload = (event) => {
        const newFileName = "paidBillImage." + event.target.files[0].name.split(".").pop();
        handleFileUpload(
            event,
            handleChange,
            () => { },
            setPaidBillImageError,
            setPaidBillImageThumbnail,
            newFileName
        );
        setPaidBillReUploaded(true);
    };

    const errorCloses = () => {
        setError("");
    };

    // Check individual document flags for rejected status
    const isCnicFrontRejected = documents?.applicantCNICFrontFlag === -1 || documents?.applicantCNICFrontFlag === "-1";
    const isCnicBackRejected = documents?.applicantCNICBackFlag === -1 || documents?.applicantCNICBackFlag === "-1";
    const isPaidBillRejected = documents?.paidBillImageFlag === -1 || documents?.paidBillImageFlag === "-1";

    // Check individual document flags for approved status
    const isCnicFrontApproved = documents?.applicantCNICFrontFlag === 1 || documents?.applicantCNICFrontFlag === "1";
    const isCnicBackApproved = documents?.applicantCNICBackFlag === 1 || documents?.applicantCNICBackFlag === "1";
    const isPaidBillApproved = documents?.paidBillImageFlag === 1 || documents?.paidBillImageFlag === "1";

    // Render document status icon
    const renderStatusIcon = (isApproved, isRejected, isReUploaded) => {
        if (isReUploaded) {
            return <CheckCircleIcon sx={{ color: "#1976d2", fontSize: "20px", marginLeft: "5px" }} />;
        }
        if (isApproved) {
            return <CheckCircleIcon sx={{ color: "green", fontSize: "20px", marginLeft: "5px" }} />;
        }
        if (isRejected) {
            return <CancelIcon sx={{ color: "red", fontSize: "20px", marginLeft: "5px" }} />;
        }
        return null;
    };

    if (documentsLoading) {
        return (
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                open={true}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    const hasRejectedCnicFront = isCnicFrontRejected && !cnicFrontReUploaded;
    const hasRejectedCnicBack = isCnicBackRejected && !cnicBackReUploaded;
    const hasRejectedPaidBill =
        documents?.paidBillImage &&
        isPaidBillRejected &&
        !paidBillReUploaded;

    const disableSubmit =
        hasRejectedCnicFront ||
        hasRejectedCnicBack ||
        hasRejectedPaidBill;

    return (
        <>
            <Grid container className="nC">
                <Box className="nCContainer">
                    <Typography variant="h6" className="heading">
                        Adjust Last Bill - Re-Upload Documents
                    </Typography>
                    <Box className="progressBarBackground">
                        <Box className="progressBarContainer">
                            <Box className="pbDocGroup">
                                <Typography className="badge">
                                    {(applicantCnicDocumentFrontThumbnail && applicantCnicDocumentBackThumbnail) ? (
                                        <Box className="backgroundAttached">
                                            <DoneIcon className="icon" />
                                        </Box>
                                    ) : (
                                        <p className="backgroundUnAttached">1</p>
                                    )}
                                </Typography>
                                <Typography className="badgeText">Applicant's CNIC.</Typography>
                            </Box>
                            {documents?.paidBillImage && (
                                <Box className="pbDocGroup">
                                    <Typography className="badge">
                                        {paidBillImageThumbnail ? (
                                            <Box className="backgroundAttached">
                                                <DoneIcon className="icon" />
                                            </Box>
                                        ) : (
                                            <p className="backgroundUnAttached">2</p>
                                        )}
                                    </Typography>
                                    <Typography className="badgeText">Paid Bill Image</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <form className="ncform">
                        <Typography variant="h6" className="groupTitle">
                            Application Details
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <Typography className="fieldLabel">Application Id</Typography>
                                <TextField
                                    required
                                    type="text"
                                    name="iv_application_id"
                                    value={dialogRowData?.applicationId || ""}
                                    disabled
                                    id="iv_application_id"
                                    className="textInputField"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <Typography className="fieldLabel">Reference Number</Typography>
                                <TextField
                                    type="text"
                                    value={dialogRowData?.referenceNo || "-"}
                                    disabled
                                    className="textInputField"
                                    name="iv_reference_number"
                                    id="iv_reference_number"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <Typography className="fieldLabel">Applicant Name</Typography>
                                <TextField
                                    type="text"
                                    name="iv_applicant_name"
                                    value={dialogRowData?.name || dialogRowData?.applicantName || "-"}
                                    disabled
                                    id="iv_applicant_name"
                                    className="textInputField"
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="h6" className="groupTitle">
                            Documents
                        </Typography>

                        {/* CNIC Front */}

                        <Grid
                            container
                            spacing={1}
                            sx={{
                                flexDirection: "row",
                                flexWrap: "nowrap",
                                overflowX: "auto"
                            }}
                        >
                            <Grid container spacing={1} className="documentsContainer" sx={{ marginBottom: "15px" }}>
                                <Grid item lg={2}>
                                    <Typography className="documentLabel" sx={{ display: "flex", alignItems: "center" }}>
                                        1- CNIC Front
                                        {renderStatusIcon(isCnicFrontApproved, isCnicFrontRejected, cnicFrontReUploaded)}
                                    </Typography>
                                </Grid>
                                <Grid item lg={2}>
                                    {applicantCnicDocumentFrontThumbnail ? (
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item>
                                                <img
                                                    src={applicantCnicDocumentFrontThumbnail}
                                                    style={{
                                                        width: "60px",
                                                        height: "60px",
                                                        objectFit: "cover",
                                                        border: isCnicFrontRejected && !cnicFrontReUploaded ? "2px solid red" : isCnicFrontApproved ? "2px solid green" : "1px solid #ccc",
                                                        borderRadius: "5px"
                                                    }}
                                                    alt="CNIC Front"
                                                />
                                            </Grid>
                                            {/* Show Change button only for rejected documents */}
                                            {isCnicFrontRejected && (
                                                <Grid item>
                                                    <Button
                                                        variant="outlined"
                                                        component="label"
                                                        size="small"
                                                        color={cnicFrontReUploaded ? "success" : "error"}
                                                        startIcon={cnicFrontReUploaded ? <CheckCircleIcon /> : <FileUploadOutlinedIcon />}
                                                    >
                                                        {cnicFrontReUploaded ? "Changed" : "Change"}
                                                        <input
                                                            hidden
                                                            accept={documentExtensions}
                                                            id="iv_applicant_cnic_document_front"
                                                            name="iv_applicant_cnic_document_front"
                                                            type="file"
                                                            onChange={ApplicantCnicDocumentFront}
                                                        />
                                                    </Button>
                                                    <Box className="documentError">{applicantCnicFrontError}</Box>
                                                </Grid>
                                            )}
                                            {isCnicFrontApproved && (
                                                <Grid item>
                                                    <Typography sx={{ color: "green", fontSize: "12px" }}>Approved</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    ) : (
                                        <>
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<FileUploadOutlinedIcon />}
                                            >
                                                Upload Front
                                                <input
                                                    hidden
                                                    accept={documentExtensions}
                                                    id="iv_applicant_cnic_document_front"
                                                    name="iv_applicant_cnic_document_front"
                                                    type="file"
                                                    onChange={ApplicantCnicDocumentFront}
                                                />
                                            </Button>
                                            <Box className="documentError">{applicantCnicFrontError}</Box>
                                        </>
                                    )}
                                </Grid>
                            </Grid>

                            {/* CNIC Back */}
                            <Grid container spacing={1} className="documentsContainer" sx={{ marginBottom: "15px" }}>
                                <Grid item lg={2}>
                                    <Typography className="documentLabel" sx={{ display: "flex", alignItems: "center" }}>
                                        2- CNIC Back
                                        {renderStatusIcon(isCnicBackApproved, isCnicBackRejected, cnicBackReUploaded)}
                                    </Typography>
                                </Grid>
                                <Grid item lg={2}>
                                    {applicantCnicDocumentBackThumbnail ? (
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item>
                                                <img
                                                    src={applicantCnicDocumentBackThumbnail}
                                                    style={{
                                                        width: "60px",
                                                        height: "60px",
                                                        objectFit: "cover",
                                                        border: isCnicBackRejected && !cnicBackReUploaded ? "2px solid red" : isCnicBackApproved ? "2px solid green" : "1px solid #ccc",
                                                        borderRadius: "5px"
                                                    }}
                                                    alt="CNIC Back"
                                                />
                                            </Grid>
                                            {/* Show Change button only for rejected documents */}
                                            {isCnicBackRejected && (
                                                <Grid item>
                                                    <Button
                                                        variant="outlined"
                                                        component="label"
                                                        size="small"
                                                        color={cnicBackReUploaded ? "success" : "error"}
                                                        startIcon={cnicBackReUploaded ? <CheckCircleIcon /> : <FileUploadOutlinedIcon />}
                                                    >
                                                        {cnicBackReUploaded ? "Changed" : "Change"}
                                                        <input
                                                            hidden
                                                            accept={documentExtensions}
                                                            id="iv_applicant_cnic_document_back"
                                                            name="iv_applicant_cnic_document_back"
                                                            type="file"
                                                            onChange={ApplicantCnicDocumentBack}
                                                        />
                                                    </Button>
                                                    <Box className="documentError">{applicantCnicBackError}</Box>
                                                </Grid>
                                            )}
                                            {isCnicBackApproved && (
                                                <Grid item>
                                                    <Typography sx={{ color: "green", fontSize: "12px" }}>Approved</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    ) : (
                                        <>
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<FileUploadOutlinedIcon />}
                                            >
                                                Upload Back
                                                <input
                                                    hidden
                                                    accept={documentExtensions}
                                                    id="iv_applicant_cnic_document_back"
                                                    name="iv_applicant_cnic_document_back"
                                                    type="file"
                                                    onChange={ApplicantCnicDocumentBack}
                                                />
                                            </Button>
                                            <Box className="documentError">{applicantCnicBackError}</Box>
                                        </>
                                    )}
                                </Grid>
                            </Grid>

                            {/* Paid Bill Image */}
                            {documents?.paidBillImage && (
                                <Grid container spacing={1} className="documentsContainer" sx={{ marginBottom: "15px" }}>
                                    <Grid item lg={2}>
                                        <Typography className="documentLabel" sx={{ display: "flex", alignItems: "center" }}>
                                            3- Paid Bill Image
                                            {renderStatusIcon(isPaidBillApproved, isPaidBillRejected, paidBillReUploaded)}
                                        </Typography>
                                    </Grid>
                                    <Grid item lg={2}>
                                        {paidBillImageThumbnail ? (
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item>
                                                    <img
                                                        src={paidBillImageThumbnail}
                                                        style={{
                                                            width: "60px",
                                                            height: "60px",
                                                            objectFit: "cover",
                                                            border: isPaidBillRejected && !paidBillReUploaded ? "2px solid red" : isPaidBillApproved ? "2px solid green" : "1px solid #ccc",
                                                            borderRadius: "5px"
                                                        }}
                                                        alt="Paid Bill"
                                                    />
                                                </Grid>
                                                {/* Show Change button only for rejected documents */}
                                                {isPaidBillRejected && (
                                                    <Grid item>
                                                        <Button
                                                            variant="outlined"
                                                            component="label"
                                                            size="small"
                                                            color={paidBillReUploaded ? "success" : "error"}
                                                            startIcon={paidBillReUploaded ? <CheckCircleIcon /> : <FileUploadOutlinedIcon />}
                                                        >
                                                            {paidBillReUploaded ? "Changed" : "Change"}
                                                            <input
                                                                hidden
                                                                accept={documentExtensions}
                                                                id="iv_paid_bill_image"
                                                                name="iv_paid_bill_image"
                                                                type="file"
                                                                onChange={PaidBillImageUpload}
                                                            />
                                                        </Button>
                                                        <Box className="documentError">{paidBillImageError}</Box>
                                                    </Grid>
                                                )}
                                                {isPaidBillApproved && (
                                                    <Grid item>
                                                        <Typography sx={{ color: "green", fontSize: "12px" }}>Approved</Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    startIcon={<FileUploadOutlinedIcon />}
                                                >
                                                    Upload
                                                    <input
                                                        hidden
                                                        accept={documentExtensions}
                                                        id="iv_paid_bill_image"
                                                        name="iv_paid_bill_image"
                                                        type="file"
                                                        onChange={PaidBillImageUpload}
                                                    />
                                                </Button>
                                                <Box className="documentError">{paidBillImageError}</Box>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            )}

                            {error && (
                                <Error
                                    error={error}
                                    onClose={() => errorCloses()}
                                    backgroundColor="#FFC0CB"
                                    textColor="#bb0303"
                                    iconColor="#bb0303"
                                />
                            )}

                        </Grid>


                        <Box sx={{ marginTop: "5%", textAlign: "right" }}>
                            <Button
                                sx={{ width: "10%", marginRight: "1%", marginBottom: "1%" }}
                                variant="outlined"
                                onClick={() => navigate("/adjust-last-bill-table")}
                            >
                                Cancel
                            </Button>
                            <Button
                                sx={{ width: "10%", marginBottom: "1%" }}
                                variant="contained"
                                component="label"
                                type="submit"
                                disabled={disableSubmit}
                                onClick={handleSubmit}
                            >
                                Submit
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Grid>

            <Snackbar
                open={openAlert}
                autoHideDuration={4000}
                onClose={() => setOpenAlert(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    severity={alertData.type}
                    variant="filled"
                    onClose={() => setOpenAlert(false)}
                >
                    {alertData.message}
                </Alert>
            </Snackbar>

            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                open={viewBackdrop}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
};

export default AdjustLastBillUpdateDocuments;
